import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { Calculator, Plus, X, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const Field = ({ label, children, className }) => (
  <div className={className}>
    <label className="text-slate-400 text-xs font-medium mb-1 block">{label}</label>
    {children}
  </div>
);
const Input = (props) => <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />;

const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';

export default function SimulationPage() {
  const { mpFetch } = useMoanaPaye();
  const [mode, setMode] = useState('libre');
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [form, setForm] = useState({
    salaire_base: 316850, heures_mensuelles: 169,
    date_anciennete: '2020-01-01', date_calcul: '2026-04-01',
    heures_sup_semaines: [], primes: [], avantages_nature: [],
    jours_maladie: 0, numero_arret_annee: 1,
    salarie_id: '', mois: new Date().getMonth() + 1, annee: 2026,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCPS, setShowCPS] = useState(true);
  const [showCST, setShowCST] = useState(true);

  useEffect(() => {
    mpFetch('/api/moana-paye/entreprises').then(d => {
      setEntreprises(d);
      if (d.length) { setSelectedEnt(d[0].id); }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedEnt) mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/salaries`).then(setSalaries).catch(console.error);
  }, [selectedEnt]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addHS = () => set('heures_sup_semaines', [...form.heures_sup_semaines, 39]);
  const removeHS = (i) => set('heures_sup_semaines', form.heures_sup_semaines.filter((_, idx) => idx !== i));
  const updateHS = (i, v) => { const arr = [...form.heures_sup_semaines]; arr[i] = parseFloat(v) || 0; set('heures_sup_semaines', arr); };

  const addPrime = () => set('primes', [...form.primes, { libelle: '', montant: 0 }]);
  const removePrime = (i) => set('primes', form.primes.filter((_, idx) => idx !== i));
  const updatePrime = (i, k, v) => { const arr = [...form.primes]; arr[i] = { ...arr[i], [k]: k === 'montant' ? parseFloat(v) || 0 : v }; set('primes', arr); };

  const calculer = async () => {
    setLoading(true);
    try {
      let data;
      if (mode === 'salarie' && form.salarie_id) {
        data = await mpFetch('/api/moana-paye/simulation/salarie', {
          method: 'POST',
          body: JSON.stringify({
            salarie_id: form.salarie_id, mois: form.mois, annee: form.annee,
            heures_sup_semaines: form.heures_sup_semaines.length > 0 ? form.heures_sup_semaines : null,
            primes: form.primes.length > 0 ? form.primes : null,
            jours_maladie: form.jours_maladie > 0 ? form.jours_maladie : null,
            numero_arret_annee: form.numero_arret_annee,
          })
        });
      } else {
        data = await mpFetch('/api/moana-paye/simulation/bulletin', {
          method: 'POST',
          body: JSON.stringify({
            salaire_base: form.salaire_base, heures_mensuelles: form.heures_mensuelles,
            date_anciennete: form.date_anciennete, date_calcul: form.date_calcul,
            heures_sup_semaines: form.heures_sup_semaines.length > 0 ? form.heures_sup_semaines : null,
            primes: form.primes.length > 0 ? form.primes : null,
            avantages_nature: form.avantages_nature.length > 0 ? form.avantages_nature : null,
            jours_maladie: form.jours_maladie > 0 ? form.jours_maladie : null,
            numero_arret_annee: form.numero_arret_annee,
          })
        });
      }
      setResult(data);
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  return (
    <div data-testid="mp-simulation" className="max-w-6xl mx-auto">
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <Calculator className="w-5 h-5 text-[#E8A020]" /> Simulation de bulletin
      </h2>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-6 bg-[#1a2332] rounded-lg p-1 border border-white/10 w-fit">
        <button onClick={() => setMode('libre')} className={`px-4 py-2 text-sm rounded-md ${mode === 'libre' ? 'bg-[#2D5A5A] text-white' : 'text-slate-400'}`}>Simulation libre</button>
        <button onClick={() => setMode('salarie')} className={`px-4 py-2 text-sm rounded-md ${mode === 'salarie' ? 'bg-[#2D5A5A] text-white' : 'text-slate-400'}`}>Par salarie</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LEFT: Saisie */}
        <div className="lg:col-span-2 space-y-4">
          {/* Base */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-4">
            <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-3">Base</h3>
            {mode === 'salarie' ? (
              <div className="space-y-3">
                {entreprises.length > 1 && <Field label="Entreprise"><select value={selectedEnt} onChange={e => setSelectedEnt(e.target.value)} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">{entreprises.map(e => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}</select></Field>}
                <Field label="Salarie"><select value={form.salarie_id} onChange={e => set('salarie_id', e.target.value)} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"><option value="">Selectionner</option>{salaries.map(s => <option key={s.id} value={s.id}>{s.prenom} {s.nom} - {s.salaire_base ? fmt(s.salaire_base) + ' F' : '-'}</option>)}</select></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Mois"><Input type="number" min={1} max={12} value={form.mois} onChange={e => set('mois', parseInt(e.target.value))} /></Field>
                  <Field label="Annee"><Input type="number" value={form.annee} onChange={e => set('annee', parseInt(e.target.value))} /></Field>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Field label="Salaire de base (F CFP)"><Input type="number" value={form.salaire_base} onChange={e => set('salaire_base', parseFloat(e.target.value))} data-testid="mp-sim-salaire" /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Heures/mois"><Input type="number" value={form.heures_mensuelles} onChange={e => set('heures_mensuelles', parseFloat(e.target.value))} /></Field>
                  <Field label="Date anciennete"><Input type="date" value={form.date_anciennete} onChange={e => set('date_anciennete', e.target.value)} /></Field>
                </div>
                <Field label="Date calcul"><Input type="date" value={form.date_calcul} onChange={e => set('date_calcul', e.target.value)} /></Field>
              </div>
            )}
          </div>

          {/* Heures sup */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Heures supplementaires</h3>
              <button onClick={addHS} className="text-[#2D5A5A] hover:text-[#E8A020] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Semaine</button>
            </div>
            {form.heures_sup_semaines.length === 0 ? <p className="text-slate-600 text-xs">Aucune heure sup</p> : (
              <div className="space-y-2">
                {form.heures_sup_semaines.map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-slate-500 text-xs w-16">Sem {i + 1}</span>
                    <Input type="number" step="0.5" value={h} onChange={e => updateHS(i, e.target.value)} className="flex-1" />
                    <span className="text-slate-500 text-xs">h</span>
                    <button onClick={() => removeHS(i)} className="text-slate-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Primes */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Primes</h3>
              <button onClick={addPrime} className="text-[#2D5A5A] hover:text-[#E8A020] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
            </div>
            {form.primes.length === 0 ? <p className="text-slate-600 text-xs">Aucune prime</p> : (
              <div className="space-y-2">
                {form.primes.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={p.libelle} onChange={e => updatePrime(i, 'libelle', e.target.value)} placeholder="Libelle" className="flex-1" />
                    <Input type="number" value={p.montant} onChange={e => updatePrime(i, 'montant', e.target.value)} className="w-28" />
                    <button onClick={() => removePrime(i)} className="text-slate-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Maladie */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-4">
            <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-3">Absences maladie</h3>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Jours d'arret"><Input type="number" min={0} value={form.jours_maladie} onChange={e => set('jours_maladie', parseInt(e.target.value) || 0)} /></Field>
              <Field label="N arret (annee)"><Input type="number" min={1} value={form.numero_arret_annee} onChange={e => set('numero_arret_annee', parseInt(e.target.value) || 1)} /></Field>
            </div>
          </div>

          <button onClick={calculer} disabled={loading} className="w-full bg-[#2D5A5A] hover:bg-[#3a7272] text-white font-medium py-3 rounded-xl text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="mp-sim-calculer">
            <Calculator className="w-4 h-4" />
            {loading ? 'Calcul en cours...' : 'Calculer le bulletin'}
          </button>
        </div>

        {/* RIGHT: Resultat */}
        <div className="lg:col-span-3">
          {!result ? (
            <div className="bg-[#1a2332] border border-white/10 rounded-xl p-10 text-center h-full flex items-center justify-center">
              <div>
                <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Saisissez les parametres puis cliquez "Calculer"</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              {result.salarie && (
                <div className="bg-[#2D5A5A]/20 border border-[#2D5A5A]/30 rounded-xl px-4 py-3">
                  <span className="text-white font-medium text-sm">{result.salarie.prenom} {result.salarie.nom}</span>
                  <span className="text-slate-400 text-sm ml-2">— {result.salarie.poste} — {result.periode}</span>
                </div>
              )}

              {/* Brut */}
              <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-white/3">
                  <span className="text-white font-semibold text-sm">Salaire brut</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {result.lignes_brut.map((l, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="px-4 py-2 text-white">{l.rubrique}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{l.base}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{l.taux}</td>
                        <td className={`px-4 py-2 text-right font-mono ${l.montant < 0 ? 'text-red-400' : 'text-white'}`}>{fmt(l.montant)} F</td>
                      </tr>
                    ))}
                    <tr className="bg-white/5">
                      <td colSpan={3} className="px-4 py-2 text-white font-semibold">TOTAL BRUT</td>
                      <td className="px-4 py-2 text-right font-mono text-[#E8A020] font-bold">{fmt(result.total_brut)} F</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* CPS */}
              <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => setShowCPS(!showCPS)} className="w-full px-4 py-3 border-b border-white/10 bg-white/3 flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">Cotisations CPS</span>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-xs">Sal: {fmt(result.total_cotisations_salariales)} F | Pat: {fmt(result.total_cotisations_patronales)} F</span>
                    {showCPS ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </button>
                {showCPS && (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10">
                      <th className="text-left px-4 py-2 text-slate-500 text-xs">Rubrique</th>
                      <th className="text-right px-4 py-2 text-slate-500 text-xs">Base</th>
                      <th className="text-right px-4 py-2 text-slate-500 text-xs">Part salariale</th>
                      <th className="text-right px-4 py-2 text-slate-500 text-xs">Part patronale</th>
                    </tr></thead>
                    <tbody>
                      {result.lignes_cps.map((l, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="px-4 py-2 text-white">{l.libelle}</td>
                          <td className="px-4 py-2 text-right text-slate-400 font-mono text-xs">{fmt(l.base)} F</td>
                          <td className="px-4 py-2 text-right text-white font-mono">{l.montant_salarial > 0 ? fmt(l.montant_salarial) + ' F' : '-'}</td>
                          <td className="px-4 py-2 text-right text-slate-400 font-mono">{fmt(l.montant_patronal)} F</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* CST */}
              <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => setShowCST(!showCST)} className="w-full px-4 py-3 border-b border-white/10 bg-white/3 flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">CST (Contribution de Solidarite Territoriale)</span>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-xs">{fmt(result.total_cst)} F</span>
                    {showCST ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </button>
                {showCST && result.lignes_cst.length > 0 && (
                  <table className="w-full text-sm">
                    <tbody>
                      {result.lignes_cst.map((l, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="px-4 py-2 text-white">Tranche {l.tranche}</td>
                          <td className="px-4 py-2 text-right text-slate-400 font-mono text-xs">{fmt(l.base)} F</td>
                          <td className="px-4 py-2 text-right text-slate-500 text-xs">{l.taux}%</td>
                          <td className="px-4 py-2 text-right text-white font-mono">{fmt(l.montant)} F</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Maladie detail */}
              {result.detail_maladie && (
                <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 text-sm">
                  <h4 className="text-orange-400 font-semibold mb-2 text-xs uppercase tracking-wider">Detail maladie</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-slate-500">Jours d'arret</span><span className="text-white">{result.detail_maladie.nb_jours_arret}</span>
                    <span className="text-slate-500">Carence</span><span className="text-white">{result.detail_maladie.jours_carence} jours</span>
                    <span className="text-slate-500">Maintien employeur</span><span className="text-white">{result.detail_maladie.jours_payes_employeur} jours</span>
                    <span className="text-slate-500">Retenue nette</span><span className="text-white">{fmt(result.detail_maladie.retenue_nette)} F</span>
                  </div>
                </div>
              )}

              {/* NET */}
              <div className="bg-[#2D5A5A] rounded-xl p-5 text-center">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Net a payer</p>
                <p className="text-3xl font-bold text-white" data-testid="mp-sim-net">{fmt(result.net_a_payer)} <span className="text-lg text-white/70">F CFP</span></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
